import {
    Card,
    CardContent,
    Typography,
    Chip,
    CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { formatTimestamp, maskId, getStatusColor } from '../../../utils/formatters';
import { dashboardAPI } from '../../../utils/dashboardAPI.js';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../AuthContext';

const MonitoredShiftsTable = ({ autoRefresh }) => {
    const { token } = useAuth();

    // shifts change less frequently: poll every 30s, staleTime 60s
    const { data: shiftsData, isFetching } = useQuery({
        queryKey: [ 'shifts' ],
        queryFn: () => dashboardAPI.getShifts(token),
        staleTime: 60000,
        retry: 1,
        refetchInterval: autoRefresh ? 30000 : false
    });

    const shifts = Array.isArray(shiftsData) ? shiftsData : [];
    const columns = [
        {
            field: 'event',
            headerName: 'Event',
            width: 120,
            valueFormatter: (value) => maskId(value)
        },
        {
            field: 'name',
            headerName: 'Name',
            width: 200,
            valueFormatter: (value) => value || 'Unnamed Shift'
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => (
                <Chip
                    label={ params.value || 'unknown' }
                    color={ getStatusColor(params.value) }
                    size="small"
                />
            )
        },
        {
            field: 'start',
            headerName: 'Start Time',
            width: 180,
            valueFormatter: (value) => formatTimestamp(value)
        },
        {
            field: 'end',
            headerName: 'End Time',
            width: 180,
            valueFormatter: (value) => formatTimestamp(value)
        },
        {
            field: 'patrollerCount',
            headerName: 'Patrollers',
            width: 120,
            valueFormatter: (value) => {
                const count = value || 0;
                return `${ count } assigned`;
            }
        }
    ];

    return (
        <Card sx={ { mb: 3 } }>
            <CardContent>
                <div style={ { display: 'flex', alignItems: 'center', gap: 8 } }>
                    <Typography variant="h6" gutterBottom sx={ { mr: 1 } }>
                        Monitored Shifts
                    </Typography>
                    { isFetching && (
                        <Chip
                            size="small"
                            icon={ <CircularProgress size={ 14 } /> }
                            label="Updating…"
                            variant="outlined"
                        />
                    ) }
                </div>
                <div style={ { height: 300, width: '100%' } }>
                    <DataGrid
                        rows={ shifts || [] }
                        columns={ columns }
                        pagination
                        paginationMode="client"
                        pageSizeOptions={ [ 10, 25, 50 ] }
                        initialState={ {
                            pagination: { paginationModel: { pageSize: 10 } },
                            sorting: { sortModel: [ { field: 'start', sort: 'asc' } ] }
                        } }
                        disableRowSelectionOnClick
                        sx={ {
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #e0e0e0'
                            }
                        } }
                    />
                </div>
            </CardContent>
        </Card>
    );
};

export default MonitoredShiftsTable;
